import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Link, useParams, useHistory } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import PageLayout from '../common/PageLayout'
import PageHeading from '../common/PageHeading'
import Divider from '../../../../components/atoms/Divider'
import Loading from '../../../../components/molecules/Loading'
import FlexRow from '../../../../components/molecules/FlexRow'
import SydButton from '../../../../components/commonDesign/Button'
import Icon from '../../../../components/atoms/Icon'
import FastAvatar from '../../../../components/molecules/FastAvatar'
import { useClient } from '../../../../api/clients'
import WYSIWYGEditor from '../../../../components/organisms/WYSIWYGEditor'
import { useDenaliActions } from '../../../../components/organisms/DenaliActionPipeline/provider/DenaliActionProvider'
import { useGetMeeting, useGetMeetingTranscript, useUpdateMeetingNotes } from '../../../../api/meetings'
import MeetingStatusBadge from './MeetingStatusBadge'
import { TERMINAL_STATUSES } from './meetingConstants'

dayjs.extend(utc)

const useStyles = makeStyles(() => ({
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr',
    gap: '8px 16px',
    fontSize: 'var(--font-size-sm)'
  },
  infoLabel: {
    color: 'var(--dai-color-text-dim)',
    fontWeight: 500
  },
  link: {
    color: 'var(--color-primary-600)',
    textDecoration: 'none',
    wordBreak: 'break-all',
    '&:hover': { textDecoration: 'underline' }
  },
  summaryCard: {
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-md)',
    overflow: 'hidden'
  },
  summaryHeader: {
    padding: '10px 14px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '&:hover': { backgroundColor: 'var(--dai-color-hover)' }
  },
  summaryBody: {
    padding: '0 14px 14px',
    fontSize: 'var(--font-size-sm)',
    lineHeight: 1.7,
    '& p': { margin: '0 0 8px' },
    '& ul, & ol': { margin: '0 0 8px', paddingLeft: '20px' },
    '& li': { marginBottom: '4px' }
  },
  statusDot: {
    width: 8, height: 8, borderRadius: '50%', display: 'inline-block', flexShrink: 0
  },
  eventsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 'var(--font-size-xs)',
    marginTop: 'var(--margin-sm)',
    '& th': {
      textAlign: 'left',
      padding: '6px 10px',
      fontWeight: 600,
      color: 'var(--dai-color-text-dim)',
      borderBottom: '1px solid var(--dai-color-border1)'
    },
    '& td': {
      padding: '5px 10px',
      borderBottom: '1px solid var(--dai-color-border1)',
      color: 'var(--dai-color-text-dim)'
    },
    '& tr:last-child td': {
      borderBottom: 'none'
    }
  },
  eventType: {
    fontFamily: 'monospace',
    fontSize: 'var(--font-size-xxs)'
  },
  breadcrumb: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--dai-color-text-dim)',
    textDecoration: 'none',
    '&:hover': { color: 'var(--color-primary-600)', textDecoration: 'underline' }
  },
  notesEditor: {
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-md)',
    minHeight: 140
  },
  saveStatusHint: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--dai-color-text-dim)',
    marginLeft: 4
  },
  toggleBtn: {
    cursor: 'pointer',
    userSelect: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: 'var(--border-radius-md)',
    border: 'var(--border-neutral-thin)',
    backgroundColor: 'transparent',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    color: 'var(--dai-color-text)',
    transition: 'background-color 150ms ease',
    '&:hover': { backgroundColor: 'var(--dai-color-hover)' }
  }
}))

const PLATFORM_LABELS = {
  teams: 'Teams', zoom: 'Zoom', google_meet: 'Google Meet', webex: 'Webex', unknown: 'Meeting'
}

const AUTOSAVE_DELAY = 1500

const STATUS_COLORS = {
  pending: 'var(--color-warning-500)',
  processing: 'var(--color-info-500)',
  completed: 'var(--color-success-500)',
  failed: 'var(--color-danger-500)'
}

function MeetingDetail () {
  const classes = useStyles()
  const { meetingId } = useParams()
  const history = useHistory()
  const { data: meeting, isLoading, isError } = useGetMeeting(meetingId)
  const { data: client } = useClient(meeting?.clientId, { enabled: !!meeting?.clientId })
  const threadId = meeting?.metadata?.threadId ?? null
  const isTerminal = meeting ? TERMINAL_STATUSES.includes(meeting.status) : false
  const isEditable = meeting ? ['pending', 'scheduled', 'completed', 'failed', 'cancelled'].includes(meeting.status) : false
  const { open: openAction } = useDenaliActions()

  const { mutateAsync: updateMeetingNotes } = useUpdateMeetingNotes()
  const [saveStatus, setSaveStatus] = useState(null)
  const notesRef = useRef(null)
  const lastSavedRef = useRef(null)
  const debounceRef = useRef(null)
  const inflightRef = useRef(null)
  const dirty = useRef(false)
  const initialized = useRef(false)

  const doSave = useCallback(async () => {
    const value = notesRef.current
    if (value == null || !dirty.current) return
    setSaveStatus('saving')
    try {
      await updateMeetingNotes({ meetingId, userNotes: value })
      lastSavedRef.current = value
      if (notesRef.current === value) {
        dirty.current = false
      }
      setSaveStatus('saved')
    } catch {
      setSaveStatus('error')
    }
  }, [updateMeetingNotes, meetingId])

  const handleNotesChange = useCallback((md) => {
    notesRef.current = md
    if (!initialized.current) {
      initialized.current = true
      lastSavedRef.current = md
      return
    }
    if (md === lastSavedRef.current) return
    dirty.current = true
    setSaveStatus(null)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null
      const p = doSave()
      inflightRef.current = p
      p.finally(() => { inflightRef.current = null })
    }, AUTOSAVE_DELAY)
  }, [doSave])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const [showEvents, setShowEvents] = useState(false)
  const [showTranscript, setShowTranscript] = useState(false)
  const { data: transcriptData } = useGetMeetingTranscript(meetingId, { enabled: showTranscript })
  const [expandedSummaries, setExpandedSummaries] = useState({})

  const toggleSummary = useCallback((id) => {
    setExpandedSummaries(prev => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const meetingVariables = useMemo(() => {
    if (!meeting) return {}
    return {
      meetingId: meeting.meetingId,
      clientId: meeting.clientId,
      title: meeting.title,
      meetingType: meeting.meetingType,
      meetingUrl: meeting.meetingUrl,
      startAt: meeting.startAt || meeting.joinAt,
      endAt: meeting.endAt,
      status: meeting.status,
      botName: meeting.botName,
      userNotes: meeting.userNotes
    }
  }, [meeting])

  const handleCancel = useCallback(() => {
    openAction('denali-cancel-meeting', meetingVariables, meetingVariables)
  }, [openAction, meetingVariables])

  const handleEdit = useCallback(() => {
    openAction('denali-update-meeting', meetingVariables, meetingVariables)
  }, [openAction, meetingVariables])

  const events = useMemo(() => meeting?.events || [], [meeting?.events])

  if (isLoading) {
    return <PageLayout variant='constrained'><Loading /></PageLayout>
  }

  if (isError || !meeting) {
    return (
      <PageLayout variant='constrained'>
        <Link to='/denali-ai/meetings' className={classes.breadcrumb}>Meetings</Link>
        <PageHeading title='Meeting Not Found' />
        <Divider />
        <p style={{ color: 'var(--dai-color-text-dim)', fontSize: 'var(--font-size-sm)', marginTop: 'var(--margin-md)' }}>
          This meeting could not be loaded. It may have been deleted or you may not have access.
        </p>
        <SydButton variant='outline' size='sm' onClick={() => history.push('/denali-ai/meetings')} style={{ marginTop: 'var(--margin-md)' }}>
          Back to Meetings
        </SydButton>
      </PageLayout>
    )
  }

  const platform = PLATFORM_LABELS[meeting.platform] || PLATFORM_LABELS.unknown

  return (
    <PageLayout variant='constrained'>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Link to='/denali-ai/meetings' className={classes.breadcrumb}>Meetings</Link>
          <PageHeading title={meeting.title || 'Untitled Meeting'}>
            <FlexRow className='gap-sm'>
              {threadId && (
                <SydButton variant='outline' size='sm' onClick={() => history.push(`/denali-ai/chat/${threadId}`)}>
                  View Thread
                </SydButton>
              )}
              {!isTerminal && (
                <SydButton variant='ghost' size='sm' onClick={handleCancel}>
                  Cancel
                </SydButton>
              )}
              {isEditable && (
                <SydButton variant='outline' size='sm' onClick={handleEdit}>
                  Edit
                </SydButton>
              )}
            </FlexRow>
          </PageHeading>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <div className={classes.infoGrid}>
            {client && (
              <>
                <span className={classes.infoLabel}>Client</span>
                <FlexRow className='gap-xs' style={{ gap: 6 }}>
                  <FastAvatar avatarUrl={client.avatarUrl} abbreviation={client.clientAbbreviation} size='xxs' />
                  <span>{client.shortName}</span>
                </FlexRow>
              </>
            )}
            <span className={classes.infoLabel}>Status</span>
            <span><MeetingStatusBadge status={meeting.status} /></span>
            <span className={classes.infoLabel}>Platform</span>
            <span>{platform}</span>
            <span className={classes.infoLabel}>Meeting URL</span>
            <a href={meeting.meetingUrl} target='_blank' rel='noopener noreferrer' className={classes.link}>
              {meeting.meetingUrl}
            </a>
            <span className={classes.infoLabel}>Notetaker</span>
            <span>{meeting.botName}</span>
            {meeting.startAt && (
              <>
                <span className={classes.infoLabel}>Scheduled</span>
                <span>{dayjs.utc(meeting.startAt).local().format('MMM D, YYYY h:mm A')}</span>
              </>
            )}
            {meeting.endAt && (
              <>
                <span className={classes.infoLabel}>End Time</span>
                <span>{dayjs.utc(meeting.endAt).local().format('MMM D, YYYY h:mm A')}</span>
              </>
            )}
            {meeting.durationSeconds > 0 && (
              <>
                <span className={classes.infoLabel}>Duration</span>
                <span>{Math.floor(meeting.durationSeconds / 60)}m {meeting.durationSeconds % 60}s</span>
              </>
            )}
            {meeting.meetingType && (
              <>
                <span className={classes.infoLabel}>Type</span>
                <span>{meeting.meetingType}</span>
              </>
            )}
          </div>
        </Grid>

        <Grid item xs={12}><Divider /></Grid>
        <Grid item xs={12}>
          <FlexRow className='gap-sm items-center' style={{ marginBottom: 8 }}>
            <span className='font-size-md font-weight-bold'>My Notes</span>
            {saveStatus === 'saving' && <span className={classes.saveStatusHint}>Saving...</span>}
            {saveStatus === 'saved' && <span className={classes.saveStatusHint} style={{ color: 'var(--color-success-500)' }}>Saved</span>}
            {saveStatus === 'error' && <span className={classes.saveStatusHint} style={{ color: 'var(--color-danger-500)' }}>Save failed</span>}
          </FlexRow>
          <div className={classes.notesEditor}>
            <WYSIWYGEditor
              placeholder='Type your meeting notes here...'
              defaultValue={meeting?.userNotes || ''}
              onChange={handleNotesChange}
              isMarkdown
              enableAutofocus={false}
            />
          </div>
        </Grid>

        {meeting.summaries?.length > 0 && (
          <>
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <span className='font-size-md font-weight-bold'>Summaries</span>
            </Grid>
            {meeting.summaries?.map(s => {
              const isOpen = expandedSummaries[s.meetingSummaryId] ?? s.status === 'completed'
              const dotColor = STATUS_COLORS[s.status] || 'var(--color-neutral-400)'

              return (
                <Grid item xs={12} key={s.meetingSummaryId}>
                  <div className={classes.summaryCard}>
                    <div className={classes.summaryHeader} onClick={() => toggleSummary(s.meetingSummaryId)}>
                      <FlexRow className='gap-sm'>
                        <span className={classes.statusDot} style={{ backgroundColor: dotColor }} />
                        <span className='font-size-sm font-weight-bold'>{s.type}</span>
                        {s.status !== 'completed' && (
                          <span className='font-size-xs' style={{ color: 'var(--dai-color-text-dim)' }}>
                            {s.status === 'pending' ? 'Pending' : s.status === 'processing' ? 'Processing...' : 'Failed'}
                          </span>
                        )}
                      </FlexRow>
                      <Icon name={isOpen ? 'expandLess' : 'expandMore'} customSize={16} />
                    </div>
                    {isOpen && s.summaryText && (
                      <div className={classes.summaryBody}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{s.summaryText}</ReactMarkdown>
                      </div>
                    )}
                    {isOpen && s.status === 'failed' && s.errorMessage && (
                      <div className={classes.summaryBody} style={{ color: 'var(--color-danger-500)' }}>
                        {s.errorMessage}
                      </div>
                    )}
                  </div>
                </Grid>
              )
            })}
          </>
        )}

        {TERMINAL_STATUSES.includes(meeting.status) && (
          <>
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <button type='button' className={classes.toggleBtn} onClick={() => setShowTranscript(prev => !prev)}>
                <Icon name={showTranscript ? 'expandLess' : 'expandMore'} customSize={16} />
                {showTranscript ? 'Hide Transcript' : 'Show Transcript'}
              </button>
              {showTranscript && transcriptData?.available && (
                <div className='font-size-sm mt-2' style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, maxHeight: 500, overflow: 'auto' }}>
                  {transcriptData.transcriptText}
                </div>
              )}
              {showTranscript && transcriptData && !transcriptData.available && (
                <div className='font-size-sm mt-2' style={{ color: 'var(--dai-color-text-dim)' }}>
                  <em>Transcript not yet available</em>
                </div>
              )}
            </Grid>
          </>
        )}

        <Grid item xs={12}><Divider /></Grid>
        <Grid item xs={12}>
          <button type='button' className={classes.toggleBtn} onClick={() => setShowEvents(prev => !prev)}>
            <Icon name={showEvents ? 'expandLess' : 'expandMore'} customSize={16} />
            {showEvents ? 'Hide Events' : 'Show Events'} ({events.length})
          </button>
          {showEvents && (
            <table className={classes.eventsTable}>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Source</th>
                  <th>Event</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {events.map(evt => (
                  <tr key={evt.meetingEventId}>
                    <td style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{dayjs.utc(evt.createdDate).local().format('h:mm:ss A')}</td>
                    <td>{evt.eventSource}</td>
                    <td className={classes.eventType}>{evt.eventType}</td>
                    <td>{evt.status && <MeetingStatusBadge status={evt.status} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Grid>
      </Grid>
    </PageLayout>
  )
}

export default MeetingDetail
